// Audio track languages: normalise ISO 639-1/639-2 codes and track titles ("English Dub") to one 2-letter
// code, and pick the track for a preferred language. Pure - shared by the server and the web player.

const ISO3: Record<string, string> = {
  eng: "en", jpn: "ja", ger: "de", deu: "de", fre: "fr", fra: "fr", spa: "es", ita: "it", por: "pt",
  rus: "ru", chi: "zh", zho: "zh", cmn: "zh", yue: "zh", kor: "ko", hin: "hi", ara: "ar", pol: "pl",
  dut: "nl", nld: "nl", swe: "sv", nor: "no", nob: "no", nno: "no", dan: "da", fin: "fi", tur: "tr",
  tha: "th", vie: "vi", ind: "id", may: "ms", msa: "ms", heb: "he", gre: "el", ell: "el", cze: "cs",
  ces: "cs", hun: "hu", rum: "ro", ron: "ro", ukr: "uk", tam: "ta", tel: "te", fil: "tl", tgl: "tl",
  ice: "is", isl: "is", est: "et", lav: "lv", lit: "lt", slo: "sk", slk: "sk", slv: "sl", hrv: "hr",
  srp: "sr", bul: "bg", per: "fa", fas: "fa", ben: "bn", urd: "ur", cat: "ca", baq: "eu", eus: "eu",
};

const NAMES: Record<string, string> = {
  en: "English", ja: "Japanese", de: "German", fr: "French", es: "Spanish", it: "Italian", pt: "Portuguese",
  ru: "Russian", zh: "Chinese", ko: "Korean", hi: "Hindi", ar: "Arabic", pl: "Polish", nl: "Dutch",
  sv: "Swedish", no: "Norwegian", da: "Danish", fi: "Finnish", tr: "Turkish", th: "Thai", vi: "Vietnamese",
  id: "Indonesian", ms: "Malay", he: "Hebrew", el: "Greek", cs: "Czech", hu: "Hungarian", ro: "Romanian",
  uk: "Ukrainian", ta: "Tamil", te: "Telugu", tl: "Filipino", is: "Icelandic", et: "Estonian", lv: "Latvian",
  lt: "Lithuanian", sk: "Slovak", sl: "Slovenian", hr: "Croatian", sr: "Serbian", bg: "Bulgarian",
  fa: "Persian", bn: "Bengali", ur: "Urdu", ca: "Catalan", eu: "Basque",
};

/** extra words in track titles that name a language */
const TITLE_WORDS: [RegExp, string][] = [
  [/\b(castellano|latino|latin american)\b/i, "es"],
  [/\b(brazilian|portugu[eê]s)\b/i, "pt"],
  [/\b(mandarin|cantonese)\b/i, "zh"],
  [/\b(nihongo)\b/i, "ja"],
];

/** "jpn" / "ja" / "en-US" -> "ja" / "en"; undefined for und/unknown */
export function langCode(s?: string): string | undefined {
  if (!s) return undefined;
  const x = s.trim().toLowerCase().split(/[-_]/)[0]!;
  if (x.length === 2 && NAMES[x]) return x;
  return ISO3[x];
}

/** a track's language: its tag, else a language named in its title ("English Dub", "Japanese 5.1") */
export function trackLang(t: { lang?: string; title?: string }): string | undefined {
  const l = langCode(t.lang);
  if (l) return l;
  const title = t.title ?? "";
  for (const [code, name] of Object.entries(NAMES)) if (new RegExp(`\\b${name}\\b`, "i").test(title)) return code;
  for (const [re, code] of TITLE_WORDS) if (re.test(title)) return code;
  // a lone "Dub" without a language is the English dub in practically every anime release
  if (/\bdub(bed)?\b/i.test(title)) return "en";
  return undefined;
}

export const langName = (code?: string) => (code ? NAMES[code] ?? code : undefined);

const COMMENTARY = /commentary|director|descriptive|audio description|\bAD\b|visually impaired/i;

/** Index (among audio streams) of the best track in language `want`, or undefined if none has it.
 *  Commentary / audio-description tracks only if nothing else matches; then the file's default, then the first. */
export function trackForLang<T extends { index: number; lang?: string; title?: string; default: boolean }>(
  tracks: T[],
  want?: string,
): number | undefined {
  const code = langCode(want);
  if (!code) return undefined;
  const hits = tracks.filter((t) => trackLang(t) === code);
  if (!hits.length) return undefined;
  const main = hits.filter((t) => !COMMENTARY.test(t.title ?? ""));
  const pool = main.length ? main : hits;
  return (pool.find((t) => t.default) ?? pool[0])!.index;
}

/** Label for the audio menu: "Japanese · 5.1 · E-AC-3", plus the title when it says more than the language. */
export function audioLabel(t: { index: number; lang?: string; title?: string; codec: string; channels?: number; atmos?: boolean }): string {
  const name = langName(trackLang(t));
  let title = (t.title ?? "").trim();
  if (name && title.toLowerCase() === name.toLowerCase()) title = "";
  const ch = t.channels ? (t.channels >= 8 ? "7.1" : t.channels >= 6 ? "5.1" : t.channels === 1 ? "mono" : "stereo") : "";
  const codec = { aac: "AAC", ac3: "DD", eac3: "DD+", truehd: "TrueHD", dts: "DTS", flac: "FLAC", opus: "Opus", mp3: "MP3", vorbis: "Vorbis" }[t.codec] ?? t.codec;
  return [name ?? t.lang ?? `Track ${t.index + 1}`, title, ch, codec, t.atmos ? "Atmos" : ""].filter(Boolean).join(" · ");
}
